import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import api, { downloadServerFile, fetchServerFileUrl } from "@/lib/api";
import { todayNZ, ymd, fmtTime, fmtDMY } from "@/lib/format";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { EstimateEditsCard } from "@/components/EstimateEditsCard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SampleDataBadge } from "@/components/SampleDataBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";
import { Loader2, BarChart3, Timer, Gauge, UserCog, FileDown, Eye, KeyRound, ChevronDown, ChevronRight, LogOut, Clock } from "lucide-react";

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
}

function weekBounds(offsetWeeks = 0) {
  const d = new Date();
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(d);
  monday.setDate(d.getDate() - dow + offsetWeeks * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [ymd(monday), ymd(sunday)];
}

const toMin = (s) => Math.round((s || 0) / 60);

export default function Reports() {
  const { t } = useTranslation();
  const [start, setStart] = useState(daysAgo(6));
  const [end, setEnd] = useState(todayNZ());
  const [rType, setRType] = useState("summary");
  const [rDetail, setRDetail] = useState("summary");
  const [picked, setPicked] = useState([]); // selected cleaner ids (for By cleaner)
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // { url, filename }
  const [showKeys, setShowKeys] = useState(false);
  const [showEdits, setShowEdits] = useState(false);
  const [activePreset, setActivePreset] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", start, end],
    queryFn: async () => (await api.get(`/reports?start=${start}&end=${end}`)).data,
  });

  const { data: roster = [] } = useQuery({
    queryKey: ["reports-roster"],
    queryFn: async () => (await api.get(`/cleaners`)).data,
  });

  async function applyPreset(preset) {
    setActivePreset(preset);
    if (preset === "today") {
      setStart(todayNZ()); setEnd(todayNZ());
    } else if (preset === "this-week") {
      const [s, e] = weekBounds(0); setStart(s); setEnd(e);
    } else if (preset === "last-week") {
      const [s, e] = weekBounds(-1); setStart(s); setEnd(e);
    } else if (preset === "last-30") {
      setStart(daysAgo(29)); setEnd(todayNZ());
    } else if (preset === "pay-period") {
      try {
        const p = (await api.get(`/timesheets/period?date=${todayNZ()}`)).data;
        if (p.period_start) setStart(p.period_start);
        if (p.period_end) setEnd(p.period_end);
      } catch {
        toast.error(t("Could not load the current pay period."));
      }
    }
  }

  function togglePick(id) {
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  // Build the report path, or return null (with a toast) if the selection is invalid.
  function reportPath() {
    let path = `/reports/pdf?start=${start}&end=${end}&type=${rType}&detail=${rDetail}`;
    if (rType === "cleaner") {
      if (picked.length === 0) {
        toast.error(t("Pick at least one cleaner, or choose 'All cleaners'."));
        return null;
      }
      path += `&cleaner_ids=${picked.join(",")}`;
    }
    return path;
  }

  async function downloadReport() {
    const path = reportPath();
    if (!path) return;
    setDownloading(true);
    try {
      await downloadServerFile(path);
      toast.success(t("Report downloaded."));
    } catch {
      toast.error(t("Could not generate the report."));
    } finally {
      setDownloading(false);
    }
  }

  async function previewReport() {
    const path = reportPath();
    if (!path) return;
    setPreviewing(true);
    try {
      const res = await fetchServerFileUrl(path);
      setPreview(res);
    } catch {
      toast.error(t("Could not generate the report."));
    } finally {
      setPreviewing(false);
    }
  }

  function closePreview() {
    if (preview?.url) window.URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  function downloadFromPreview() {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview.url;
    a.download = preview.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const cleanerData = (data?.cleaners || []).map((c) => ({
    name: c.cleaner_name,
    Estimated: toMin(c.estimated_seconds),
    Actual: toMin(c.actual_seconds),
    efficiency: c.efficiency,
  }));
  const roomData = (data?.rooms || []).map((r) => ({
    name: r.early_checkout ? `${r.room_number} ⤴` : r.room_number,
    Estimated: toMin(r.estimated_seconds),
    Actual: toMin(r.actual_seconds),
  }));
  const earlyCheckoutRooms = (data?.rooms || []).filter((r) => r.early_checkout).map((r) => r.room_number);
  const dailyData = (data?.daily || []).map((d) => ({
    name: d.date.slice(5),
    Estimated: toMin(d.estimated_seconds),
    Actual: toMin(d.actual_seconds),
    Rooms: d.rooms,
  }));

  const totalActual = (data?.cleaners || []).reduce((s, c) => s + c.actual_seconds, 0);
  const totalEst = (data?.cleaners || []).reduce((s, c) => s + c.estimated_seconds, 0);
  const overallEff = totalActual > 0 ? Math.round((totalEst / totalActual) * 100) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-3xl font-extrabold">{t("Reports")}</h1>
            <SampleDataBadge />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("Actual vs estimated productivity across cleaners, rooms and days.")}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <DatePicker value={start} onChange={(v) => { setStart(v); setActivePreset(null); }} testid="report-start-picker" />
          <span className="pb-2 text-muted-foreground">→</span>
          <DatePicker value={end} onChange={(v) => { setEnd(v); setActivePreset(null); }} testid="report-end-picker" />
        </div>
      </div>

      <Card className="rounded-md border p-4" data-testid="report-download-panel">
        <div className="mb-3 flex items-center gap-2">
          <FileDown className="h-4 w-4 text-muted-foreground" />
          <span className="font-display text-lg font-extrabold">{t("Download PDF report")}</span>
        </div>

        <div className="mb-3 flex flex-wrap gap-2">
          {[
            ["today", t("Today")],
            ["this-week", t("This week")],
            ["last-week", t("Last week")],
            ["pay-period", t("This pay period")],
            ["last-30", t("Last 30 days")],
          ].map(([key, label]) => {
            const active = activePreset === key;
            return (
              <Button
                key={key}
                variant={active ? "default" : "outline"}
                size="sm"
                onClick={() => applyPreset(key)}
                className={active
                  ? "bg-teal-600 text-white border-teal-600 shadow-sm ring-2 ring-teal-300 hover:bg-teal-700 hover:text-white"
                  : ""}
                aria-pressed={active}
                data-testid={`preset-${key}`}
              >
                {label}
              </Button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="label-caps mb-1 block text-muted-foreground">{t("Report")}</label>
            <Select value={rType} onValueChange={(v) => setRType(v)}>
              <SelectTrigger data-testid="report-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">{t("Overall summary")}</SelectItem>
                <SelectItem value="room">{t("By room")}</SelectItem>
                <SelectItem value="cleaner">{t("By cleaner / group")}</SelectItem>
                <SelectItem value="all">{t("All cleaners")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps mb-1 block text-muted-foreground">{t("Detail")}</label>
            <Select value={rDetail} onValueChange={(v) => setRDetail(v)}>
              <SelectTrigger data-testid="report-detail-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">{t("Summary (totals only)")}</SelectItem>
                <SelectItem value="detailed">{t("Detailed (per-day line items)")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              variant="outline"
              onClick={previewReport}
              disabled={previewing || downloading}
              className="w-full gap-2"
              data-testid="preview-report-btn"
            >
              {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} {t("Preview")}
            </Button>
            <Button onClick={downloadReport} disabled={downloading || previewing} className="w-full gap-2" data-testid="download-report-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} {t("Download")}
            </Button>
          </div>
        </div>

        {rType === "cleaner" && (
          <div className="mt-3" data-testid="report-cleaner-picker">
            <label className="label-caps mb-1.5 block text-muted-foreground">{t("Pick cleaner(s)")}</label>
            <div className="flex flex-wrap gap-2">
              {roster.length === 0 ? (
                <span className="text-sm text-muted-foreground">{t("No cleaners found.")}</span>
              ) : (
                roster.map((c) => {
                  const on = picked.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => togglePick(c.id)}
                      data-testid={`pick-cleaner-${c.id}`}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        on
                          ? "border-indigo-400 bg-indigo-50 font-semibold text-indigo-700"
                          : "border-border text-muted-foreground hover:bg-secondary"
                      }`}
                    >
                      {c.name}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          {t("Includes rooms cleaned, estimated vs actual time, efficiency, hours worked, and rooms that consistently run over/under estimate with a suggested new estimate. Filename carries the report type, date range and the time it was generated.")}
        </p>
      </Card>

      <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent
          className="flex h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0"
          data-testid="report-preview-dialog"
        >
          <DialogTitle className="sr-only">{t("Report preview")}</DialogTitle>
          <DialogDescription className="sr-only">{t("Preview of the generated PDF report; use Download to save it.")}</DialogDescription>
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5 pr-12">
            <div className="min-w-0 pr-2">
              <div className="label-caps text-muted-foreground">{t("Report preview")}</div>
              <div className="truncate text-sm font-semibold" data-testid="preview-filename">
                {preview?.filename}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={downloadFromPreview} className="gap-1.5" data-testid="preview-download-btn">
                <FileDown className="h-4 w-4" /> {t("Download")}
              </Button>
            </div>
          </div>
          {preview?.url && (
            <iframe
              src={preview.url}
              title="Report preview"
              className="min-h-0 flex-1 w-full"
              data-testid="preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>


      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <SummaryTile icon={Timer} label={t("Total Clean Time")} value={`${toMin(totalActual)} min`} />
            <SummaryTile icon={Gauge} label={t("Total Estimated")} value={`${toMin(totalEst)} min`} />
            <SummaryTile
              icon={BarChart3}
              label={t("Overall Efficiency")}
              value={overallEff != null ? `${overallEff}%` : "—"}
              hint={t("Estimated ÷ Actual")}
            />
          </div>

          <ChartCard title={t("Cleaner — Actual vs Estimated (min)")} testid="chart-cleaners">
            {cleanerData.length ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={cleanerData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Estimated" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Actual" fill="#DC2626" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty />
            )}
          </ChartCard>

          {(data?.cleaners || []).length > 0 && (
            <Card className="rounded-md border p-4" data-testid="cleaner-efficiency-section">
              <div className="mb-1 flex items-center gap-2">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                <span className="font-display text-lg font-extrabold">{t("Per-cleaner efficiency")}</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {t("Efficiency = estimated ÷ actual. Over 100% is faster than estimated; under 100% is slower. Shared tasks split the estimate pro-rata by each person's actual time.")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cleaner-efficiency-table">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3 font-semibold">{t("Cleaner")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Rooms")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Estimated")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Actual")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Efficiency")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(data?.cleaners || [])].sort((a, b) => (b.efficiency || 0) - (a.efficiency || 0)).map((c) => {
                      const e = c.efficiency;
                      const cls = e == null ? "text-muted-foreground" : e >= 100 ? "text-emerald-700" : e >= 85 ? "text-amber-600" : "text-red-700";
                      return (
                        <tr key={c.cleaner_id} className="border-b last:border-0" data-testid={`cleaner-eff-row-${c.cleaner_id}`}>
                          <td className="py-2 pr-3 font-medium">{c.cleaner_name}</td>
                          <td className="py-2 pr-3">{c.rooms}</td>
                          <td className="py-2 pr-3">{toMin(c.estimated_seconds)} min</td>
                          <td className="py-2 pr-3">{toMin(c.actual_seconds)} min</td>
                          <td className={`py-2 pr-3 font-bold ${cls}`} data-testid={`cleaner-eff-${c.cleaner_id}`}>{e == null ? "—" : `${e}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChartCard title={t("Room — Estimated vs Actual (min)")} testid="chart-rooms">
              {roomData.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={roomData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Estimated" fill="#65A30D" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Actual" fill="#0f172a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Empty />
              )}
              {earlyCheckoutRooms.length > 0 && (
                <div className="mt-3 border-t border-dashed border-border pt-3" data-testid="reports-early-checkouts">
                  <div className="mb-1.5 text-xs font-semibold text-muted-foreground">{t("Early checkouts this period (extra full clean after service)")}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {earlyCheckoutRooms.map((rn) => (
                      <span
                        key={rn}
                        data-testid={`reports-early-checkout-${rn}`}
                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
                      >
                        <LogOut className="h-3 w-3" /> {rn}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </ChartCard>

            <ChartCard title={t("Daily Productivity (min)")} testid="chart-daily">
              {dailyData.length ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="Estimated" stroke="#6366F1" strokeWidth={2} />
                    <Line type="monotone" dataKey="Actual" stroke="#DC2626" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Empty />
              )}
            </ChartCard>
          </div>

          {(data?.daily || []).some((d) => d.finish) && (
            <Card className="rounded-md border p-4" data-testid="finish-log-section">
              <div className="mb-1 flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="font-display text-lg font-extrabold">{t("Finish Log — projected vs actual")}</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {t("Planned finish = the planned/rostered start time plus the day's estimated workload shared across the cleaners who worked, plus their entitled breaks. A positive variance means the day ran slower than planned.")}
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="finish-log-table">
                  <thead>
                    <tr className="border-b border-border text-left text-xs text-muted-foreground">
                      <th className="py-2 pr-3 font-semibold">{t("Date")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Started")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Planned finish")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Actual finish")}</th>
                      <th className="py-2 pr-3 font-semibold">{t("Variance")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(data.daily || []).filter((d) => d.finish).map((d) => {
                      const f = d.finish;
                      const v = f.finish_variance_minutes;
                      let vLabel = "—", vClass = "text-muted-foreground";
                      if (v != null) {
                        if (v > 5) { vLabel = `+${v} min slow`; vClass = "text-red-600 font-semibold"; }
                        else if (v < -5) { vLabel = `${Math.abs(v)} min early`; vClass = "text-emerald-600 font-semibold"; }
                        else { vLabel = t("On time"); vClass = "text-emerald-600 font-semibold"; }
                      }
                      return (
                        <tr key={d.date} data-testid={`finish-log-row-${d.date}`}>
                          <td className="py-2 pr-3 font-semibold">{fmtDMY(d.date)}</td>
                          <td className="py-2 pr-3 text-muted-foreground">{fmtTime(f.first_start)}</td>
                          <td className="py-2 pr-3 text-indigo-600 font-semibold">{fmtTime(f.projected_finish)}</td>
                          <td className="py-2 pr-3">{f.actual_finish ? fmtTime(f.actual_finish) : "—"}</td>
                          <td className={`py-2 pr-3 ${vClass}`} data-testid={`finish-log-variance-${d.date}`}>{vLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {(data?.managers || []).length > 0 && (
            <Card className="rounded-md border p-4" data-testid="manager-cleaning-section">
              <div className="mb-1 flex items-center gap-2">
                <UserCog className="h-4 w-4 text-muted-foreground" />
                <span className="font-display text-lg font-extrabold">{t("Manager cleaning (record only)")}</span>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {t("Time managers spent cleaning rooms. Kept separate — not included in cleaner productivity or payroll.")}
              </p>
              <div className="divide-y divide-border">
                {data.managers.map((m) => (
                  <div
                    key={m.manager_id}
                    className="flex items-center justify-between py-2.5 text-sm"
                    data-testid={`manager-clean-row-${m.manager_id}`}
                  >
                    <span className="font-semibold">{m.manager_name}</span>
                    <span className="text-muted-foreground">
                      {toMin(m.actual_seconds)} min · {m.rooms} room{m.rooms === 1 ? "" : "s"}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="rounded-md border p-4" data-testid="key-return-log-section">
            <button
              type="button"
              onClick={() => setShowKeys((v) => !v)}
              data-testid="key-return-log-toggle"
              className="flex w-full items-center gap-2 text-left"
            >
              {showKeys ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <KeyRound className="h-4 w-4 text-sky-600" />
              <span className="font-display text-lg font-extrabold">{t("Key return log")}</span>
              <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">{(data?.key_returns || []).length}</span>
            </button>
            {showKeys && (
              <>
                <p className="mb-3 mt-2 text-xs text-muted-foreground">
                  {t("Who marked each departure room's key as returned, and when. \"Auto\" means the system marked it at the scheduled time.")}
                </p>
                {(data?.key_returns || []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground" data-testid="key-return-log-empty">
                    {t("No keys have been marked returned in this range yet.")}
                  </p>
                ) : (
                  <div className="divide-y divide-border">
                    {data.key_returns.map((k, i) => (
                      <div
                        key={`${k.date}-${k.room_number}-${i}`}
                        className="flex items-center justify-between gap-3 py-2.5 text-sm"
                        data-testid={`key-return-row-${k.room_number}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-display text-base font-extrabold">{k.room_number}</span>
                          {k.stripped && (
                            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">stripped</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-right">
                          <span className="text-muted-foreground">{fmtDMY(k.date)} · {fmtTime(k.at)}</span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${k.auto ? "bg-sky-100 text-sky-700" : "bg-secondary text-foreground"}`}
                          >
                            {k.auto ? t("Auto") : k.by_name}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>

          <Card className="rounded-md border p-4" data-testid="manual-edits-section">
            <button
              type="button"
              onClick={() => setShowEdits((v) => !v)}
              data-testid="manual-edits-toggle"
              className="flex w-full items-center gap-2 text-left"
            >
              {showEdits ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              <UserCog className="h-4 w-4 text-indigo-600" />
              <span className="font-display text-lg font-extrabold">{t("Manual corrections")}</span>
              <span className="ml-1 rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">{(data?.manual_edits || []).length}</span>
            </button>
            {showEdits && (
              <>
                <p className="mb-3 mt-2 text-xs text-muted-foreground">
                  {t("Every time a manager set or changed who did a task, adjusted a time, or moved time between rooms — for a full audit trail.")}
                </p>
                {(data?.manual_edits || []).length === 0 ? (
                  <p className="py-6 text-center text-sm text-muted-foreground" data-testid="manual-edits-empty">
                    {t("No manual corrections in this range.")}
                  </p>
                ) : (
                  <div className="divide-y divide-border">
                    {data.manual_edits.map((m, i) => (
                      <div
                        key={`${m.date}-${m.room_number}-${i}`}
                        className="flex items-start justify-between gap-3 py-2.5 text-sm"
                        data-testid={`manual-edit-row-${i}`}
                      >
                        <div className="min-w-0">
                          <span className="font-display font-extrabold">{t("Room {{room}}", { room: m.room_number })}</span>
                          <span className="ml-2 text-muted-foreground">{m.detail}</span>
                        </div>
                        <div className="shrink-0 text-right text-xs text-muted-foreground">
                          <div className="font-semibold text-foreground">{m.by_name}</div>
                          <div>{fmtDMY(m.at)} · {fmtTime(m.at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>

          <EstimateEditsCard start={start} end={end} />
        </>
      )}
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, hint }) {
  return (
    <Card className="rounded-md border p-4">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="label-caps">{label}</span>
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <Card className="rounded-md border p-4" data-testid={testid}>
      <div className="mb-4 font-display text-lg font-bold">{title}</div>
      {children}
    </Card>
  );
}

function Empty() {
  const { t } = useTranslation();
  return (
    <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
      {t("No data in this range yet.")}
    </div>
  );
}
